import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  GMAIL_GET_LOADING,
  GMAIL_POST_LOADING,
  GMAIL_SYNC_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { gmailApiService } from "./services/api";
import { useIntl } from "react-intl";
import {
  getGmailFailureAction,
  getGmailInitAction,
  getGmailSuccessAction,
  postBindGmailInitAction,
  postBindGmailSuccessAction,
  postUnbindGmailFailureAction,
  postUnbindGmailInitAction,
  postUnbindGmailSuccessAction,
} from "./store/api-actions";
import { settingSelector, userSettingSelector } from "./store/selector";
import { Email } from "./interface/api/post-gmail-email";
import { EmailFile } from "../email/interface/emailFile";

export function useGmailCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const settings = useSelector(settingSelector);
  const userSettings = useSelector(userSettingSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const unbind = useCallback(
    async (companyId: string, asUser: boolean = false, onSuccess?: () => void) => {
      try {
        dispatch(postUnbindGmailInitAction());
        await gmailApiService.unbind(companyId, asUser);
        dispatch(postUnbindGmailSuccessAction({ asUser }));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postUnbindGmailFailureAction({ error }));
      }
    },
    [dispatch]
  );

  const bind = useCallback(
    async (
      companyId: string,
      code: string,
      asUser: boolean = false,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postBindGmailInitAction());
        turnOnLoading(GMAIL_SYNC_LOADING);
        const response = await gmailApiService.bind(companyId, code, asUser);
        dispatch(postBindGmailSuccessAction({ settings: response, asUser }));
        turnOffLoading(GMAIL_SYNC_LOADING, 500);
        toast.success(intl.formatMessage({ id: "app.toast.sucess46" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(GMAIL_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error67" }));
        console.error(error);
        dispatch(postUnbindGmailFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const get = useCallback(
    async (companyId: string, asUser: boolean = false) => {
      try {
        dispatch(getGmailInitAction());
        turnOnLoading(GMAIL_GET_LOADING);
        const response = await gmailApiService.get(companyId, asUser);
        dispatch(getGmailSuccessAction({ settings: response, asUser }));
        turnOffLoading(GMAIL_GET_LOADING, 1500);
      } catch (error: any) {
        turnOffLoading(GMAIL_GET_LOADING);
        console.error(error);
        dispatch(getGmailFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const login = useCallback(
    async (companyId: string, asUser: boolean = false) => {
      try {
        turnOnLoading(GMAIL_SYNC_LOADING);
        const response = await gmailApiService.login(companyId, asUser);
        return response.url;
      } catch (error: any) {
        turnOffLoading(GMAIL_SYNC_LOADING);
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error68" }));
      }
    },
    [turnOffLoading, turnOnLoading, intl]
  );

  const postEmail = useCallback(
    async (
      companyId: string,
      email: Email,
      files: EmailFile[],
      asUser: boolean = false,
      cardId: string | null,
      onSuccess?: () => void
    ) => {
      try {
        turnOnLoading(GMAIL_POST_LOADING);
        const id = await gmailApiService.postEmail(companyId, email, files, asUser, cardId);
        turnOffLoading(GMAIL_POST_LOADING, 500);
        onSuccess?.();
        toast.success(intl.formatMessage({ id: "app.toast.sucess47" }));
        return id;
      } catch (error: any) {
        turnOffLoading(GMAIL_POST_LOADING);
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error69" }));
      }
    },
    [turnOffLoading, turnOnLoading, intl]
  );

  return {
    settings,
    userSettings,
    unbind,
    bind,
    get,
    login,
    postEmail,
  };
}
